import { useEffect, useState } from 'react';
import {
  Activity, AlertTriangle, RefreshCw, Webhook, Banknote, Clock,
  CheckCircle2, ExternalLink,
} from 'lucide-react';
import supabase from '../lib/supabase';
import { PageHeader, Section, Badge, Button, EmptyState } from '../components/ui';

type Summary = {
  failed_transfers: number;
  stuck_transfers: number;
  failed_webhooks: number;
  webhooks_24h: number;
};

type Transfer = {
  id: string;
  stripe_transfer_id: string | null;
  amount: number;
  currency: string;
  status: string;
  failure_reason: string | null;
  club_id: string | null;
  coach_id: string | null;
  court_booking_id: string | null;
  coach_booking_id: string | null;
  created_at: string;
  updated_at: string;
};

type Event = {
  id: string;
  type: string;
  processing_status: string;
  processing_error: string | null;
  received_at: string;
  processed_at: string | null;
};

export default function Operations() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [sumRes, xferRes, evRes] = await Promise.all([
      (supabase.from as any)('ops_exceptions_summary').select('*').maybeSingle(),
      supabase
        .from('stripe_transfers')
        .select('id, stripe_transfer_id, amount, currency, status, failure_reason, club_id, coach_id, court_booking_id, coach_booking_id, created_at, updated_at')
        .or('status.eq.failed,and(status.eq.pending,updated_at.lt.' + new Date(Date.now() - 30 * 60_000).toISOString() + ')')
        .order('updated_at', { ascending: false })
        .limit(50),
      supabase
        .from('stripe_events')
        .select('id, type, processing_status, processing_error, received_at, processed_at')
        .eq('processing_status', 'failed')
        .order('received_at', { ascending: false })
        .limit(50),
    ]);
    setSummary((sumRes.data as Summary) ?? null);
    setTransfers((xferRes.data as Transfer[]) ?? []);
    setEvents((evRes.data as Event[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        eyebrow="AceRent admin"
        title="Operations"
        subtitle="Failed Stripe transfers, stuck payouts, and webhooks that crashed mid-handler. Use the Stripe dashboard to retry — this page reads, doesn't write."
        actions={<Button variant="secondary" icon={RefreshCw} loading={loading} onClick={load}>Refresh</Button>}
      />

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard
          icon={Banknote}
          label="Failed transfers"
          value={summary?.failed_transfers ?? 0}
          tone={summary && summary.failed_transfers > 0 ? 'red' : 'gray'}
          hint="Money that didn't reach a club or coach."
        />
        <SummaryCard
          icon={Clock}
          label="Stuck transfers"
          value={summary?.stuck_transfers ?? 0}
          tone={summary && summary.stuck_transfers > 0 ? 'amber' : 'gray'}
          hint="Pending > 30min — webhook may have missed."
        />
        <SummaryCard
          icon={Webhook}
          label="Failed webhooks"
          value={summary?.failed_webhooks ?? 0}
          tone={summary && summary.failed_webhooks > 0 ? 'red' : 'gray'}
          hint="Events that threw mid-handler."
        />
        <SummaryCard
          icon={Activity}
          label="Webhooks · 24h"
          value={summary?.webhooks_24h ?? 0}
          tone="blue"
          hint="Total events received in the last day."
        />
      </div>

      {/* Failed + stuck transfers */}
      <Section
        title="Stripe transfers needing attention"
        subtitle="Failed and stuck-pending transfers. Failed = Stripe rejected the transfer; stuck = the success webhook never arrived."
        className="p-0 overflow-hidden mb-6"
      >
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading transfers…</div>
        ) : transfers.length === 0 ? (
          <EmptyState
            icon={CheckCircle2}
            title="All clear"
            description="No failed or stuck transfers in the last batch."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Created</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Amount</th>
                <th className="text-left px-5 py-3 font-semibold">Recipient</th>
                <th className="text-left px-5 py-3 font-semibold">Reason / failure</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {transfers.map((t) => (
                <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-700">
                    {new Date(t.created_at).toLocaleString('en-GB', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                    })}
                  </td>
                  <td className="px-5 py-3">
                    <Badge tone={t.status === 'failed' ? 'red' : 'amber'}>
                      {t.status === 'failed' ? 'Failed' : 'Stuck pending'}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-gray-900">
                    {t.currency} {Number(t.amount).toFixed(0)}
                  </td>
                  <td className="px-5 py-3 text-xs font-mono text-gray-500">
                    {t.coach_id ? `coach:${t.coach_id.slice(0, 8)}` :
                     t.club_id ? `club:${t.club_id.slice(0, 8)}` : '—'}
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-700 max-w-md">
                    <div className="truncate" title={t.failure_reason ?? ''}>
                      {t.failure_reason ?? (t.status === 'pending'
                        ? 'No webhook arrived to confirm. Check Stripe dashboard.'
                        : '—')}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {t.stripe_transfer_id && (
                      <a
                        href={`https://dashboard.stripe.com/connect/transfers/${t.stripe_transfer_id}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-[11px] text-[#2d5016] hover:underline inline-flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" /> Stripe
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Failed webhooks */}
      <Section
        title="Failed webhook events"
        subtitle="Stripe events the handler failed to process. Investigate the error, fix the bug, then resend the event from the Stripe dashboard."
        className="p-0 overflow-hidden"
      >
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading events…</div>
        ) : events.length === 0 ? (
          <EmptyState
            icon={CheckCircle2}
            title="No failed webhooks"
            description="Every event we've received has been processed without throwing."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Received</th>
                <th className="text-left px-5 py-3 font-semibold">Type</th>
                <th className="text-left px-5 py-3 font-semibold">Error</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-5 py-3 whitespace-nowrap text-xs text-gray-700">
                    {new Date(e.received_at).toLocaleString('en-GB', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                    })}
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-gray-700">{e.type}</td>
                  <td className="px-5 py-3 text-xs text-red-700 max-w-xl">
                    <div className="line-clamp-2" title={e.processing_error ?? ''}>
                      {e.processing_error ?? '(no error captured)'}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <a
                      href={`https://dashboard.stripe.com/events/${e.id}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-[11px] text-[#2d5016] hover:underline inline-flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" /> Stripe
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function SummaryCard({
  icon: Icon, label, value, tone, hint,
}: {
  icon: any;
  label: string;
  value: number;
  tone: 'red' | 'amber' | 'blue' | 'gray';
  hint: string;
}) {
  const toneClasses = {
    red:   'bg-red-50 border-red-200 text-red-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    blue:  'bg-blue-50 border-blue-200 text-blue-900',
    gray:  'bg-white border-gray-200 text-gray-900',
  }[tone];
  const iconClasses = {
    red: 'text-red-600', amber: 'text-amber-600', blue: 'text-blue-600', gray: 'text-gray-400',
  }[tone];
  return (
    <div className={`rounded-xl border p-4 ${toneClasses}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-5 h-5 ${iconClasses}`} />
        {value > 0 && tone !== 'gray' && tone !== 'blue' && (
          <AlertTriangle className="w-4 h-4 text-current opacity-60" />
        )}
      </div>
      <div className="text-2xl font-semibold">{value.toLocaleString()}</div>
      <div className="text-xs uppercase tracking-wider opacity-70 mt-0.5">{label}</div>
      <div className="text-xs opacity-80 mt-1.5">{hint}</div>
    </div>
  );
}
